import styled, { useTheme } from 'styled-components'
import { match } from 'ts-pattern'
import { useMemo } from 'use-memo-one'

import { Numeric, Typography } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { usePairAgeFormatted } from '@root/entities/pair/hooks'
import { Trade } from '@root/entities/trade'
import { useAppRoutes } from '@root/features/routing'
import { MarkerCapShortFormatted } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import type { ClassNameProps } from '@root/shared/react'

const POSITIVE_OPERATIONS = ['buy', 'liquidityAdd', 'poolInit'] as const

type Props = {
  trade: Trade.Trade
} & ClassNameProps

export const TrackingWalletFollowingUpdateCardBody: React.FC<Props> = ({
  className,
  trade,
}) => {
  const { t } = useTranslation()
  const { formatted: tradeAge } = usePairAgeFormatted(trade.timestamp)
  const currency =
    trade.quoteCurrency ?? CurrencyModel.nullCurrency(trade.network)
  const { navigateToTrading } = useAppRoutes()

  const makerActionType = Trade.tradeMakerActionType(trade)
  const makerActionLabel = useMemo(
    () =>
      match(makerActionType)
        .with('buy-more', () => t('trade.buy-more'))
        .with('buy-first', () => t('trade.buy-first'))
        .with('sell-all', () => t('trade.sell-all'))
        .with('sell-partial', () => t('trade.sell-partial'))
        .with('buy', () => t('trading.buy'))
        .with('sell', () => t('trading.sell'))
        .with(undefined, () => '')
        .exhaustive(),
    [makerActionType, t],
  )

  const theme = useTheme()
  const styles = {
    '--type-color': POSITIVE_OPERATIONS.includes(trade.type)
      ? theme.palette.candleGreen[9]
      : theme.palette.candleRed[9],
  } as React.CSSProperties

  return (
    <Root style={styles} className={className}>
      <FirstRow>
        <ActionContainer>
          <ActionLabel variant="body4-medium">{makerActionLabel}</ActionLabel>
          <ActionAmount variant="body4-medium">
            <Numeric
              value={trade.totalUsd}
              preset="currency-amount-short"
              currency={'$'}
            />
          </ActionAmount>
        </ActionContainer>
        <CurrencyContainer
          onClick={() => {
            navigateToTrading(trade.pairAddress, trade.network)
          }}
        >
          <CurrencyIcon
            size={22}
            auxIconSize={8}
            currency={currency}
            pairAddress={trade.pairAddress}
            withGoogleLens={false}
          />
          <CurrencyName variant="body4-medium" ellipsis>
            {currency.ticker}
          </CurrencyName>
        </CurrencyContainer>
      </FirstRow>
      <SecondRow>
        <Metrics>
          <PriceContainer>
            <Typography variant="body4-medium">{t('common.price')}</Typography>
            <Typography variant="body4-medium">
              <Numeric
                value={trade.currencyPriceUsd}
                preset="currency-amount-short"
                currency={'$'}
              />
            </Typography>
          </PriceContainer>
          <McContainer>
            <Typography variant="body4-medium">
              {t('trade.market-cap-short')}
            </Typography>
            <Typography variant="body4-medium">
              <MarkerCapShortFormatted mcap={trade.marketCapUsd} />
            </Typography>
          </McContainer>
        </Metrics>
        <Age variant="body4-medium">{tradeAge}</Age>
      </SecondRow>
    </Root>
  )
}

const Root = styled.div``

const FirstRow = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 14px;
  justify-content: start;
  margin-bottom: 6px;
`

const ActionContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 3px;
`

const ActionLabel = styled(Typography)`
  color: var(--text-9);
`

const ActionAmount = styled(Typography)`
  color: var(--type-color);
`

const CurrencyContainer = styled.div`
  cursor: pointer;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 6px;
`

const CurrencyName = styled(Typography)`
  color: var(--text-12);
`

const SecondRow = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 14px;
  justify-content: space-between;
`

const PriceContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 3px;

  color: var(--text-9);
`

const McContainer = styled(PriceContainer)`
  color: var(--amber-9);
`

const Metrics = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 14px;
`

const Age = styled(Typography)`
  color: var(--type-color);
`
